import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.pics';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Us — litbuyspreadsheets.pics',
  url: `${siteUrl}/contact`,
  description: 'Get in touch with the litbuyspreadsheets.pics team. Have questions about Litbuy Spreadsheet or our educational content? We would love to hear from you.',
};

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    formData.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    formData.append('message', (form.elements.namedItem('message') as HTMLTextAreaElement).value);

    try {
      await fetch('https://readdy.ai/api/form/d8r5ud8n4gr6p3b2gdi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      form.reset();
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>Contact Us — Litbuy Spreadsheet Guide | litbuyspreadsheets.pics</title>
      <meta name="description" content="Get in touch with the litbuyspreadsheets.pics team. Have questions about Litbuy Spreadsheet or our educational content? We would love to hear from you." />
      <link rel="canonical" href={`${siteUrl}/contact`} />
      <meta property="og:title" content="Contact Us — Litbuy Spreadsheet Guide | litbuyspreadsheets.pics" />
      <meta property="og:description" content="Get in touch with the litbuyspreadsheets.pics team. Questions about Litbuy Spreadsheet? We would love to hear from you." />
      <meta property="og:url" content={`${siteUrl}/contact`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Contact Us — Litbuy Spreadsheet Guide | litbuyspreadsheets.pics" />
      <meta name="twitter:description" content="Get in touch with the litbuyspreadsheets.pics team." />
      <JsonLd data={contactSchema} />
      <Navbar />
      <main className="pt-24 pb-16 px-6 md:px-10 lg:px-16">
        <article className="max-w-3xl mx-auto">
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full border border-accent-200 text-xs font-medium text-accent-600 tracking-wide mb-4">
              Contact
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              Get in Touch
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              Have questions about Litbuy Spreadsheet or our content? We would love to hear from you.
            </p>
          </header>

          <div className="space-y-10">
            {submitted ? (
              <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/60 text-center">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">Message Sent!</h2>
                <p className="text-sm text-foreground-500 mb-6">Thank you for reaching out. We will get back to you as soon as possible.</p>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  &larr; Back to Home
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60" data-readdy-form="">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-background-50 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="Tell us what you would like to know about Litbuy Spreadsheet..."
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">Maximum 500 characters</p>
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    Send Message
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                  </button>
                </div>
              </form>
            )}

            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">Other Ways to Reach Us</h2>
              <div className="flex flex-col gap-3 text-sm">
                <a href="mailto:hello@litbuyspreadsheets.pics" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-2 text-foreground-400">
                    <i className="ri-mail-line"></i>
                  </span>
                  hello@litbuyspreadsheets.pics
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-2 text-foreground-400">
                    <i className="ri-external-link-line"></i>
                  </span>
                  Visit Main Website &rarr;
                </a>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4 text-sm">
              <a href="/" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 border-b border-transparent hover:border-primary-300 pb-0.5 whitespace-nowrap">
                &larr; Back to Home
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}